import { Button, Toast, Toaster } from "@newsela/angelou";

import { useEffect } from "react";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import globalToasterRef from "@/base/globalToasterRef";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import ExportablesArticlePreview from "./ExportablesArticlePreview";
import {
  bodyWrapperCss,
  galleryPreviewWrapperCss,
  hideOnMobileCss,
  instructionsCss,
  loadingSpinnerWrapperCss,
  loadingWrapperCss,
  hideOnTabletAndUpCss,
} from "./ExportablesModalBody.styles";
import ExportablesTemplateGallery from "./ExportablesTemplateGallery";
import ExportablesTemplatePreview from "./ExportablesTemplatePreview";

interface ExportablesModalBodyProps {
  isStudent: boolean;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

const ExportablesModalBody = ({ isStudent }: ExportablesModalBodyProps) => {
  const {
    isInAssignGraphicOrganizersExperiment,
    isLoading,
    showAssignmentWarning,
    renderOnMobile,
    setShowAssignmentWarning,
    setRenderOnMobile,
    templatesToAssign,
  } = useExportablesContext();

  const instructions = isStudent
    ? "Your teacher has assigned you the graphic organizers below, you can type directly into them. Closing this window before submitting will delete any progress."
    : isInAssignGraphicOrganizersExperiment
      ? `Select all the graphic organizers you want to add to the assignment. You can also individually download them as PDFs. (${templatesToAssign.length} selected)`
      : null;

  useEffect(() => {
    if (showAssignmentWarning) {
      globalToasterRef.current?.displayToast({
        children:
          "You must select at least one graphic organizer to add to an assignment.",
        handleDismiss: () => setShowAssignmentWarning(false),
        showIcon: true,
        statusColor: Toast.statusColor.warning,
      });
    }
  }, [showAssignmentWarning]);

  return isLoading ? (
    <div css={loadingWrapperCss}>
      <AILoadingSpinner __cssFor={{ root: loadingSpinnerWrapperCss }} />
    </div>
  ) : (
    <div css={bodyWrapperCss}>
      <Toaster ref={globalToasterRef} />

      {instructions && (
        <p css={[instructionsCss, !renderOnMobile && hideOnMobileCss]}>
          {instructions}
        </p>
      )}
      {!renderOnMobile && (
        <p css={[instructionsCss, hideOnTabletAndUpCss]}>
          Graphic organizers are optimized for larger screens. Please try
          accessing through a bigger device.
        </p>
      )}

      <div css={[galleryPreviewWrapperCss, !renderOnMobile && hideOnMobileCss]}>
        {isStudent ? (
          <ExportablesArticlePreview />
        ) : (
          <ExportablesTemplateGallery />
        )}
        <ExportablesTemplatePreview isStudent={isStudent} />
      </div>

      {!renderOnMobile && (
        <div css={hideOnTabletAndUpCss}>
          <ButtonWithTrackEvent
            flavor={Button.flavors.secondary}
            onClick={() => setRenderOnMobile(true)}
            dataProps={{
              "data-qa-selector": "show-previous-graphic-organizer",
            }}
            objectType={objectTypes.content}
            actionName={actionNames.present}
            streamProperties={{
              description: "Show Graphic Organizer on mobile",
            }}
          >
            View anyway
          </ButtonWithTrackEvent>
        </div>
      )}
    </div>
  );
};

export default ExportablesModalBody;
